import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { BehaviorSubject, filter, Observable, switchMap } from 'rxjs';
import {
  TenantConfig,
  ViewTenantOwnerResponse,
} from '../models/TenantConfig.model';

import { Router } from '@angular/router';
import { ShellComponent } from '@adminprodigydesign/axis';
import { SubdomainService } from './subdomain.service';
import { environment } from '../../../environments/environment';

interface DeliveryRule {
  minimumDeliveryAmount: string;
  deliveryCharge: string;
  isActive: string;
  deliveryRulesId: string;
}

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  // BehaviorSubject to allow components to subscribe to updates
  private tenantConfigSubject = new BehaviorSubject<TenantConfig | null>(null);
  tenantConfig$ = this.tenantConfigSubject.asObservable();

  constructor(
    private http: HttpClient,
    public router: Router,
  ) {}

  private injectShellRouteData(config: TenantConfig) {
    const shellRoute = this.router.config.find(
      (r) => r.component === ShellComponent,
    );

    if (shellRoute) {
      shellRoute.data = {
        companyName: config.brandName,
        logo: config.logoUrl,
        navLinks: config.shellConfig?.navLinks,
        navAlignment: config.shellConfig?.navAlignment,
        stacked: config.shellConfig?.stacked,
        branding: {
          primaryColor: config.primaryColor,
          textColor: '#ffffff',
        },
        footerData: config.shellConfig?.footerData,
        showCart: config.shellConfig?.showCart,
      };
    }
  }

  loadTenantConfig(): Promise<TenantConfig> {
    const url = environment.tenantConfigUrl;
    const params = new HttpParams().set('action', 'viewTenantOwner');

    return this.http
      .get<TenantConfig>(url, { params, withCredentials: true })
      .toPromise()
      .then((config) => {
        if (!config) throw new Error('Tenant config is undefined');

        this.tenantConfigSubject.next(config);
        this.applyTheme(config);
        this.injectShellRouteData(config);
        return config;
      });
  }

  updateTenant(changed: Partial<TenantConfig>): Observable<any> {
    // match system-admin’s shape exactly:
    const body = {
      action: 'updateTenant',
      tenant: changed,
    };
    return this.http.post<any>(environment.tenantConfigUrl, body, {
      withCredentials: true,
    });
  }

  private applyTheme(config: TenantConfig) {
    const root = document.documentElement;

    root.style.setProperty('--primary-color', config.primaryColor || '#CC1B1B');
    root.style.setProperty('--text-color', config.textColor || '#ffffff');
    root.style.setProperty('--nav-links', config.navLinkColor || '#ffffff');
    root.style.setProperty(
      '--secondary-color',
      config.secondaryColor || '#f4f4f4',
    );
    root.style.setProperty(
      '--background-color',
      config.backgroundColor || '#f4f4f4',
    );
    root.style.setProperty(
      '--secondary-text-color',
      config.secondaryTextColor || '#000',
    );
    root.style.setProperty('--accent-color', config.accentColor || '#fe6301');
  }

  getTenantBusinessHours(): Observable<any> {
    const url = environment.tenantConfigUrl;
    const params = new HttpParams().set('action', 'viewBusinessHoursOwner');

    return this.http.get<TenantConfig>(url, { params });
  }

  updateBusinessHours(payload: {
    businessHoursId: number;
    hoursCategory: 'store' | 'delivery' | 'special';
    openingDay?: string;
    specificDate?: string | null;
    openTime?: string | null;
    closeTime?: string | null;
    isClosed?: number;
    isDeleted?: string;
  }): Observable<any> {
    const url = environment.tenantConfigUrl;
    const body = {
      action: 'updateBusinessHours',
      tenantBusinessHours: payload,
    };

    return this.http.post(url, body);
  }

  insertBusinessHours(businessHours: any[]): Observable<any> {
    const payload = {
      action: 'insertNewBusinessHours',
      tenantBusinessHours: businessHours,
    };
    return this.http.post<any>(environment.tenantConfigUrl, payload, {
      withCredentials: true,
    });
  }

  getTenantContact(): Observable<any> {
    const url = environment.tenantConfigUrl;
    const params = new HttpParams().set('action', 'viewTenantContactOwner');

    return this.http.get<TenantConfig>(url, { params });
  }

  /**
   * Persist updated minimum-order amount.
   */
  updateTenantPaymentSettings(settings: {
    minimumDeliveryAmount: number;
  }): Observable<any> {
    const body = {
      action: 'updateTenantPaymentSettings',
      tenantPaymentSettings: settings,
    };
    return this.http.post(environment.tenantConfigUrl, body, {
      withCredentials: true,
    });
  }

  getTenantDeliveryRules(): Observable<{
    status: string;
    message: string;
    deliveryRules: DeliveryRule[];
  }> {
    const body = { action: 'viewTenantDeliveryRulesOwner' };
    return this.http.post<{
      status: string;
      message: string;
      deliveryRules: DeliveryRule[];
    }>(environment.tenantConfigUrl, body, { withCredentials: true });
  }

  /**
   * Adds tenant delivery rules (e.g., minimum order & delivery charge).
   */
  addTenantDeliveryRules(rules: {
    minimumDeliveryAmount: number;
    deliveryCharge: number;
    isActive?: number;
  }): Observable<any> {
    const body = {
      action: 'addNewTenantDeliveryRules',
      tenantDeliveryRules: rules,
    };

    return this.http.post(environment.tenantConfigUrl, body, {
      withCredentials: true,
    });
  }

  /**
   * Updates tenant delivery rules (e.g., minimum order & delivery charge).
   */
  updateTenantDeliveryRules(
    deliveryRulesId: number,
    rules: {
      minimumDeliveryAmount: number;
      deliveryCharge: number;
      isActive: number;
    },
  ): Observable<any> {
    const body = {
      action: 'updateTenantDeliveryRules',
      deliveryRulesId,
      tenantDeliveryRules: rules,
    };

    return this.http.post(environment.tenantConfigUrl, body, {
      withCredentials: true,
    });
  }
}
